import { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api.js';
import { useAuth } from './AuthContext';

const ModuleContext = createContext();

export function ModuleProvider({ children }) {
  const { user, userRoles } = useAuth();
  const [enabledModules, setEnabledModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get the user's club ID from their roles
  const getUserClubId = () => {
    if (!userRoles || userRoles.length === 0) return null;

    // Priority: club_admin role (has club_id)
    const clubAdminRole = userRoles.find(role => role.role === 'club_admin');
    if (clubAdminRole?.club_id) return clubAdminRole.club_id;

    // Fallback: any role that has club_id directly (e.g. team_captain)
    const roleWithClubId = userRoles.find(role => role.club_id);
    if (roleWithClubId?.club_id) return roleWithClubId.club_id;

    // Fallback: any role linked to a team that knows its club
    const roleWithTeam = userRoles.find(role => role.team?.club_id);
    if (roleWithTeam?.team?.club_id) return roleWithTeam.team.club_id;

    return null;
  };

  // Fetch enabled modules for the user's club
  const fetchEnabledModules = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // System admins have access to all modules
      const isSystemAdmin = userRoles?.some(role => role.role === 'system_admin');
      if (isSystemAdmin) {
        const allModules = await api.get('/api/modules');
        setEnabledModules(allModules || []);
        setIsLoading(false);
        return;
      }

      // Get user's club ID
      const clubId = getUserClubId();
      if (!clubId) {
        // No club association - no modules available
        setEnabledModules([]);
        setIsLoading(false);
        return;
      }

      // Fetch enabled modules for this club
      const data = await api.get(`/api/modules/club?club_id=${clubId}`);

      // Filter out expired or inactive modules
      const activeModules = (data || [])
        .filter(cm => {
          if (!cm.module?.is_active) return false;
          if (cm.expires_at && new Date(cm.expires_at) < new Date()) return false;
          return true;
        })
        .map(cm => cm.module);

      setEnabledModules(activeModules);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching enabled modules:', err);
      setError(err.message);
      setEnabledModules([]);
      setIsLoading(false);
    }
  };

  // Fetch modules when user or roles change
  useEffect(() => {
    if (user && userRoles) {
      fetchEnabledModules();
    } else {
      setEnabledModules([]);
      setIsLoading(false);
    }
  }, [user, userRoles]);

  // Real-time subscription removed (Supabase realtime no longer available).
  // Module data refreshes when the user or roles change.

  const value = {
    enabledModules,
    isLoading,
    error,
    hasModule: (moduleCode) => {
      return enabledModules.some(module => module.code === moduleCode);
    },
    refreshModules: fetchEnabledModules
  };

  return (
    <ModuleContext.Provider value={value}>
      {children}
    </ModuleContext.Provider>
  );
}

export function useModules() {
  const context = useContext(ModuleContext);
  if (context === undefined) {
    throw new Error('useModules must be used within a ModuleProvider');
  }
  return context;
}
